import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, Timestamp, doc, updateDoc, serverTimestamp } from 'firebase/firestore'; // Added doc, updateDoc, serverTimestamp
import { db, auth, updateAlatGelasStockOnReturn } from '../../firebase'; // Import stock update function
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { List, ListItem, ListItemText, Button, Typography, CircularProgress, Box, Paper, Stack, Chip } from '@mui/material'; // Added Stack, Chip

// Helper to format Firestore Timestamp to readable date and time string
const formatDateTime = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    // Use options for more control over formatting
    return timestamp.toDate().toLocaleString('id-ID', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
  }
  return 'N/A';
};

const AlatSiapDigunakan = () => {
  const [approvedRequests, setApprovedRequests] = useState([]); // Renamed state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [updatingReturnId, setUpdatingReturnId] = useState(null); // State to track returning item
  const navigate = useNavigate();

  // Get current user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setError('Silakan login untuk melihat riwayat peminjaman alat.'); // Updated error message
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Function to fetch approved and returned requests
  const fetchApprovedRequests = async () => { // Renamed function
      if (!currentUser) return;

      setLoading(true);
      setError('');
      try {
        const requestsRef = collection(db, 'permintaan_alat');
      // Fetch requests with status 'approved', 'admin_approved', or 'returned' for the current user
      const q = query(
        requestsRef,
        where('userId', '==', currentUser.uid),
        where('status', 'in', ['approved', 'admin_approved', 'returned', 'cancelled', 'pending_return'])
      );

        const querySnapshot = await getDocs(q);
        let data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));

        // Sort by borrowing start date descending (newest first)
        data.sort((a, b) => {
            const timeA = a.tanggalPinjam?.seconds || 0;
            const timeB = b.tanggalPinjam?.seconds || 0;
            return timeB - timeA;
        });

        setApprovedRequests(data); // Update state with all approved/returned requests
      } catch (err) {
        console.error("Error fetching approved/returned requests:", err);
        setError('Gagal memuat data peminjaman alat.'); // Updated error message
      } finally {
        setLoading(false);
    }
  };

  // Determine if a request is currently active
  const isAktif = (request) => {
    // For alat gelas, consider pending_return as still active
    if (request.jenisAlat === 'alat_gelas' && request.status === 'pending_return') {
      return true;
    }
    // For approved or admin_approved status, always consider active
    if (request.status === 'approved' || request.status === 'admin_approved') {
      return true;
    }
    
    return false;
  };

  // Fetch requests on mount and when user changes
  useEffect(() => {
    fetchApprovedRequests(); // Call the renamed function
  }, [currentUser]);

  const handleIsiLogbook = (request) => {
    // Check if the request is for an instrument before navigating
    if (request.jenisAlat !== 'instrumen') {
        alert('Logbook hanya tersedia untuk instrumen.');
        return;
    }
    navigate('/mahasiswa/isi-logbook', {
      state: {
        alatId: request.alatId,
        namaAlat: request.namaAlat,
        permintaanId: request.id // Pass the request ID
      }
    });
  };

  // Handle returning an item
  const handleReturn = async (request) => {
    const requestId = request.id;
    setUpdatingReturnId(requestId);
    setError('');
    try {
      // Validate essential data
      if (!request || !request.id || !request.jenisAlat) {
        throw new Error("Data permintaan tidak lengkap.");
      }

      // For glassware, set status to pending_return for admin/laboran approval
      // For instruments, directly set to returned
      const newStatus = request.jenisAlat === 'alat_gelas' ? 'pending_return' : 'returned';
      
      const requestRef = doc(db, 'permintaan_alat', requestId);
      await updateDoc(requestRef, {
        status: newStatus,
        tanggalPengajuanKembali: serverTimestamp(),
        // For glassware, set default returned amount same as borrowed
        ...(request.jenisAlat === 'alat_gelas' && {
          jumlahDikembalikan: request.jumlah
        })
      });

      console.log(`Request ${requestId} status updated to ${newStatus}.`);
      fetchApprovedRequests();

    } catch (err) {
      console.error("Error processing return for item:", err);
      setError(`Gagal memproses pengembalian alat: ${err.message}`);
    } finally {
      setUpdatingReturnId(null);
    }
  };


  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Alat yang Disetujui {/* Renamed Title */}
      </Typography>
      {error && <Typography color="error">{error}</Typography>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {approvedRequests.length > 0 ? (
            approvedRequests.map((req) => {
              const aktif = isAktif(req);
              const buttonText = req.jenisAlat === 'instrumen' ? 'Selesai' : 'Kembalikan';
              const loadingText = req.jenisAlat === 'instrumen' ? 'Menyelesaikan...' : 'Mengembalikan...';

              return (
                <ListItem
                  key={req.id}
                  secondaryAction={
                    aktif ? ( // Only show buttons if active
                      <Stack direction="row" spacing={1}>
                        {req.jenisAlat === 'instrumen' && ( // Only show logbook for instruments
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleIsiLogbook(req)}
                            disabled={updatingReturnId === req.id}
                          >
                            Isi Logbook
                          </Button>
                        )}
                        {/* Pass the entire request object instead of just the ID */}
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          onClick={() => handleReturn(req)}
                          disabled={updatingReturnId === req.id}
                        >
                          {updatingReturnId === req.id ? loadingText : buttonText}
                        </Button>
                      </Stack>
                    ) : null // No actions if not active
                  }
                  divider
                >
                  <ListItemText
                    primary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {req.namaAlat || 'Nama Alat Tidak Diketahui'}
                        <Chip
                          label={
                            req.status === 'pending_return' ? 'Menunggu Persetujuan' :
                            aktif ? 'Aktif' :
                            req.status === 'cancelled' ? 'Dibatalkan' : 'Tidak Aktif'
                          }
                          color={
                            req.status === 'pending_return' ? 'warning' :
                            aktif ? 'success' :
                            req.status === 'cancelled' ? 'error' : 'default'
                          }
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={`Periode: ${formatDateTime(req.tanggalPinjam)} - ${formatDateTime(req.tanggalKembali)} ${req.status === 'returned' ? `(Selesai/Dikembalikan: ${formatDateTime(req.tanggalDikembalikanActual)})` : ''}`}
                  />
                </ListItem>
              );
            })
          ) : (
            <Typography sx={{ textAlign: 'center', my: 2 }}>
              Tidak ada riwayat peminjaman alat yang disetujui. {/* Updated empty state message */}
            </Typography>
          )}
        </List>
      )}
    </Paper>
  );
};

export default AlatSiapDigunakan;
